import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { KeyboardArrowDown, MoreHoriz } from '@mui/icons-material';
import { IconButton, Menu, MenuItem, Popper } from '@mui/material';
import { styled } from '@mui/material/styles';
import { isEmpty, isNil, sortBy } from 'lodash';
import AddMultipleMenuItem from 'components/FeaturedSpreadsheet/components/AddMultipleMenuItem';
import { SORT_DESC } from 'pages/Valuations/util/constants';
import { useResponse } from 'services/hooks';
import theme from 'theme';
import { getStringValue } from 'utilities';
import { getColumnIndex, getColumnLetter } from 'utilities/alphabet-utilities';
import { SortAscIcon, SortDescIcon } from './SortIcons';
import { ReverseParsedFundOwnershipDetail } from '../../../pages/CapTable/fund-ownership/types';
import FeaturedSpreadsheetContext from '../context/FeaturedSpreadsheetContext';
import { copyToClipboard, getCellRef, getValuesToCopy, hasCellColSpan } from '../utilities/utilities';


const StyledColumnIndicator = styled('div')(({ allowReorderColumns }) => ({
  padding: 0,
  position: 'absolute',
  top: '0',
  backgroundColor: theme.palette.primary[500],
  borderRadius: '10px 10px 0px 0px',
  height: '12px',
  textAlign: 'center',
  cursor: allowReorderColumns ? 'move' : 'default',
}));

const StyledMenuButton = styled(IconButton)(({ cursorType }) => ({
  maxWidth: '12px',
  minWidth: '12px',
  color: theme.palette.white,
  padding: '0',
  marginTop: '-14px',
  height: '12px',
  cursor: cursorType || 'default',
}));

const StyledButtonIcon = styled('div')(() => ({
  fill: `${theme.palette.white} !important`,
  width: 'auto !important',
  height: '15px !important',
}));

const StyledDeleteMenuItem = styled(MenuItem)(() => ({
  color: theme.palette.pink,
}));

const StyledMenu = styled(Menu)(() => ({
  // Add any menu-specific styling here if needed
}));

interface ColumnIndicatorProps {
  tableRef: React.RefObject<HTMLElement> | null;
  hiddenColumns?: {
    right?: number;
    left?: number;
  };
}

interface IndicatorPosition {
  left: number;
  width: number;
}

interface CellInfo {
  key?: string;
  columnLegend?: string;
  y?: number;
  columnId?: number | string;
}

interface Column {
  id?: number | string;
  order?: number;
  isLTM?: boolean;
  isNTM?: boolean;
  isDeleteableColumn?: boolean;
  isSortableColumn?: boolean;
  isStringColumn?: boolean;
  isDateColumn?: boolean;
  fields?: Array<Record<string, string>>;
  parentColumn?: any;
}


const ColumnIndicator: React.FC<ColumnIndicatorProps> = ({ tableRef, hiddenColumns }) => {
  const [showIndicator, setShowIndicator] = useState<boolean>(false);
  const [indicatorPosition, setIndicatorPosition] = useState<IndicatorPosition | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const [cell, setCell] = useState<CellInfo | null>(null);
  const [multipleValue, setMultipleValue] = useState<number>(0);
  const {
    addMultipleColumns,
    allowAddMultipleColumns,
    allowCloneColumn,
    allowConfirmAndDeleteColumn,
    allowCopyColumn,
    allowDeleteColumn,
    allowDeleteOnlySpecificColumn,
    allowReorderColumns,
    allowSortColumn,
    cells,
    cloneColumn,
    columns,
    displayColumnIndicator,
    handleDeleteColumn,
    selectedCell,
    setSelectedCell,
    sortColumnFn,
    sortedColumn,
    workbook,
  } = useContext(FeaturedSpreadsheetContext);

  const { infoNotification, errorNotification } = useResponse();

  useEffect(() => {
    if (!selectedCell || !displayColumnIndicator) {
      return;
    }
    const { start, end, cellInfo } = selectedCell;

    setCell(cellInfo);
    const { j: startColumn, i: startRow } = start;
    const { j: endColumn } = end;
    // This condition will hide the column indicator when the user has selected one or more columns.
    if (startColumn === endColumn) {
      const columnLetter = getColumnLetter(startColumn);
      const cellKey = cellInfo.key ?? `${columnLetter}${startRow}`;
      const cellRef = getCellRef(tableRef, cellKey);
      const hasColSpan = hasCellColSpan(cells, cellKey, cellRef);
      if (!hasColSpan && cellRef) {
        // Check if this cell has the projection-to-ltm divider
        const hasProjectionToLtmDivider = cellRef.classList.contains('projection-to-ltm-divider');
        
        // Check if the previous cell has the projection-to-ltm divider
        const previousCell = cellRef.previousElementSibling as HTMLElement;
        const previousHasProjectionToLtmDivider = previousCell?.classList.contains('projection-to-ltm-divider');
        
        // Check if the next cell (LTM) has the projection-to-ltm divider
        const nextCell = cellRef.nextElementSibling as HTMLElement;
        const nextHasProjectionToLtmDivider = nextCell?.classList.contains('projection-to-ltm-divider');
        
        let adjustedWidth = cellRef.offsetWidth;
        let adjustedLeft = cellRef.offsetLeft;
        
        if (hasProjectionToLtmDivider) {
          // This cell has the divider, shift its position right by 12.5px and reduce width by 12.5px on the left side
          adjustedLeft = cellRef.offsetLeft + 12.5;
          adjustedWidth = cellRef.offsetWidth - 12.5;
        } else if (previousHasProjectionToLtmDivider) {
          // The previous cell has the divider, reduce this cell's width by 12.5px on the left side
          adjustedWidth = cellRef.offsetWidth - 12.5;
        } else if (nextHasProjectionToLtmDivider) {
          // The next cell (LTM) has the divider, reduce this cell's width by 12.5px on the right side
          adjustedWidth = cellRef.offsetWidth - 12.5;
        } else {
          // For regular columns (including new projection columns), use the exact cell dimensions
          adjustedWidth = cellRef.offsetWidth;
          adjustedLeft = cellRef.offsetLeft;
        }
        
        setIndicatorPosition({
          left: adjustedLeft,
          width: adjustedWidth,
        });
        setShowIndicator(true);
      } else {
        setShowIndicator(false);
      }
    }
  }, [selectedCell, displayColumnIndicator, cells, tableRef, hiddenColumns]);

  const handleOnMoreOptionsClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    },
    [setAnchorEl]
  );

  // This uses a regular expression to replace all digits with an empty string
  const getLetterFromKey = (key?: string): string => key?.replace(/\d/g, '') ?? '';

  const onCloseDropdown = (): void => setAnchorEl(null);

  const handleOnCopyClick = useCallback(() => {
    onCloseDropdown();
    if (!cell) {
      return;
    }
    const { columnLegend, key } = cell;
    const columnLetter = columnLegend ?? getLetterFromKey(key);
    if (columnLetter) {
      const valuesToCopy = getValuesToCopy(cells, columnLetter, true);
      copyToClipboard(valuesToCopy, infoNotification, errorNotification);
    }
  }, [cell, cells, infoNotification, errorNotification]);

  const columnIndex = useMemo(() => {
    if (!cell) {
      return null;
    }
    const { y: columnPos, columnLegend, key } = cell;
    const cellColumnLegend = columnLegend ?? getLetterFromKey(key);
    const index = columnPos ?? getColumnIndex(cellColumnLegend);
    return index;
  }, [cell]);

  const showDeleteOption = useMemo(() => {
    if (
      (allowDeleteColumn || allowDeleteOnlySpecificColumn)
      && columns
      && !isNil(columnIndex)
      && columnIndex < columns.length
    ) {
      const sortedColumns = sortBy(columns, ['order']);
      const column = sortedColumns[columnIndex] as Column;
      const isLTMOrNTMColumnLegend = column?.isLTM || column?.isNTM;
      const isDeletable = !isLTMOrNTMColumnLegend && column && !('parentColumn' in column);
      const allowDelete = (allowDeleteColumn || allowConfirmAndDeleteColumn) && isDeletable;
      const allowDeleteCondition = allowDeleteOnlySpecificColumn
        ? column.isDeleteableColumn && allowDelete
        : allowDelete;
      return allowDeleteCondition;
    }
    return false;
  }, [columnIndex, allowConfirmAndDeleteColumn, allowDeleteOnlySpecificColumn, allowDeleteColumn, columns]);

  const hideIndicator = useCallback(() => {
    setSelectedCell(null);
    setIndicatorPosition(null);
    setShowIndicator(false);
  }, [setSelectedCell]);

  const getColumnId = (
    cellColumnId: number | string | undefined,
    colIndex: number | null,
    cols: ReverseParsedFundOwnershipDetail[]
  ): number | string | null => {
    if (cellColumnId) return cellColumnId;
    if (cols && colIndex !== null) {
      return cols[colIndex]?.id ?? colIndex;
    }
    return colIndex;
  };

  const handleOnDeleteClick = useCallback(() => {
    onCloseDropdown();
    if (cell) {
      const { columnId } = cell;
      const currentColumnId = getColumnId(columnId, columnIndex, columns);
      if (handleDeleteColumn && showDeleteOption) {
        // If handleDeleteColumn expects 3 arguments based on its signature, it comes from ScalarSpreadsheet
        // If so, we will pass columnIndex, columnId, wb
        if (handleDeleteColumn.length === 4 && !isNil(currentColumnId) && workbook) {
          handleDeleteColumn(columnIndex, currentColumnId, workbook, hideIndicator);
        }
        // If it expects 1 argument, it comes from FeaturedSpreadsheet
        // If so, we will pass columnIndex
        if (handleDeleteColumn.length === 2 && columnIndex !== null) {
          handleDeleteColumn(columnIndex, hideIndicator);
        }
      }
    }
  }, [cell, handleDeleteColumn, workbook, columnIndex, showDeleteOption, columns, hideIndicator]);

  const handleOnCloneClick = useCallback(() => {
    onCloseDropdown();
    if (cell && cloneColumn && columnIndex !== null) {
      if (!isNil(columnIndex)) {
        cloneColumn(columnIndex);
      }
    }
  }, [cell, cloneColumn, columnIndex]);

  const handleOnAddMultipleClick = (): void => {
    onCloseDropdown();
    if (addMultipleColumns) {
      addMultipleColumns(multipleValue);
    }
  };

  const currentColumn = columns && columnIndex !== null ? (columns[columnIndex] as Column) : null;
  const field = currentColumn?.fields?.[0];
  const columnKey = field ? Object.values(field)?.[0] : null;
  const fieldIsString = !!currentColumn?.isStringColumn;
  const fieldIsDate = !!currentColumn?.isDateColumn;

  const isSortableColumn = useMemo(
    () => !!allowSortColumn && !!currentColumn?.isSortableColumn,
    [allowSortColumn, currentColumn]
  );

  const SortDescription = useMemo(() => {
    if (isEmpty(sortedColumn)) {
      return SortDescIcon;
    }

    if (sortedColumn && columnKey && columnKey in sortedColumn) {
      return getStringValue(sortedColumn?.[columnKey]) === SORT_DESC ? SortAscIcon : SortDescIcon;
    }

    return SortDescIcon;
  }, [columnKey, sortedColumn]);

  const handleSortColumnClick = (): void => {
    onCloseDropdown();

    if (isSortableColumn && columnKey && sortColumnFn) {
      sortColumnFn(columnKey, fieldIsString, fieldIsDate);
    }
  };

  // With this function we will determine if do we have to show or not the dropdown menu on the column indicator
  const showMenuOptions = useMemo(
    () => allowCopyColumn || allowReorderColumns || showDeleteOption || isSortableColumn,
    [allowCopyColumn, allowReorderColumns, isSortableColumn, showDeleteOption]
  );

  return (
    <StyledColumnIndicator
      id={`column-indicator-${(cell?.columnLegend ?? '') + 0}`}
      className={allowReorderColumns ? 'column-rearrange' : ''}
      allowReorderColumns={allowReorderColumns}
      style={{ left: indicatorPosition?.left, width: indicatorPosition?.width }}>
      {showIndicator && allowReorderColumns && (
        <StyledMenuButton cursorType="move">
          <StyledButtonIcon as={MoreHoriz} fontSize="small" />
        </StyledMenuButton>
      )}
      {showIndicator && showMenuOptions && (
        <>
          <StyledMenuButton cursorType="pointer" onClick={handleOnMoreOptionsClick}>
            <StyledButtonIcon as={KeyboardArrowDown} fontSize="small" />
          </StyledMenuButton>
          <Popper open={open} anchorEl={anchorEl} id={open ? 'transitions-popper' : undefined}>
            <StyledMenu
              getContentAnchorEl={null}
              anchorEl={anchorEl}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
              PaperProps={{
                style: {
                  minWidth: indicatorPosition?.width,
                },
              }}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={onCloseDropdown}>
              {allowCopyColumn && (
                <MenuItem onClick={handleOnCopyClick} disableRipple>
                COPY
                </MenuItem>
              )}
              {allowCloneColumn && (
                <MenuItem onClick={handleOnCloneClick} disableRipple>
                CLONE
                </MenuItem>
              )}
              {allowAddMultipleColumns && addMultipleColumns && (
                <AddMultipleMenuItem
                  onAddMultiple={handleOnAddMultipleClick}
                  multipleValue={multipleValue}
                  setMultipleValue={setMultipleValue}
                />
              )}
              {showDeleteOption && (
                <StyledDeleteMenuItem onClick={handleOnDeleteClick}>
                DELETE
                </StyledDeleteMenuItem>
              )}

              {isSortableColumn && (
                <MenuItem onClick={handleSortColumnClick}>
                  <SortDescription />
                </MenuItem>
              )}
            </StyledMenu>
          </Popper>
        </>
      )}
    </StyledColumnIndicator>
  );
};

export default ColumnIndicator;